import React, { useState } from 'react';
import styled from 'styled-components';
import { ButtonSwitch } from 'components';
import { Tag } from 'components/Tag';
import { theme, toRem } from 'design';
import { ITipsBreakdownLineItem } from 'screens/MakePayment/Calculator/types';
import {
  CalculatorSplitMethod,
  DEFAULT_POOL_NAME,
  ICalculatorRule,
  ICategoryGroup,
  ITipCalculatorResultContribution,
  ITipCalculatorResultEmployee,
  RuleFundSource,
  ShareEligibilityMethod,
  ShareRemainderType,
  ShareRuleType,
  TipCalculatorReceivedDistributionType,
} from 'types';
import { buildSalesItems, centsToDollarsDisplay, Duration, getTipSources, toPercent, toTitleCase } from 'utils';
import ContributingBreakdown from './ContributingBreakdown';
import ReceivingBreakdown from './ReceivingBreakdown';

interface IToggleableBreakdownProps {
  categoryGroups: ICategoryGroup[];
  calculatorRules: ICalculatorRule[];
  calculation: ITipCalculatorResultEmployee;
  creditCardFee?: number;
  className?: string;
  contributingOnly?: boolean;
  dataCy?: string;
  segmentName: string;
  disabled?: boolean;
}

const BreakdownContainer = styled.div``;

const StyledButtonSwitch = styled(ButtonSwitch)`
  @media (max-width: 500px) {
    justify-content: space-between;
  }

  @media (max-width: 350px) {
    button {
      padding: ${toRem(4)} ${toRem(8)};
      font-size: ${toRem(10)};
    }
  }
`;

const ContributingTag = styled(Tag)`
  color: ${theme.color.white};
  background-color: ${theme.color.secondary[400]};
  border-radius: ${toRem(4)};
  padding: ${toRem(6)} ${toRem(12)};
  font-size: ${toRem(11)};
`;

const ToggleableBreakdownLayout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  padding: ${toRem(16)};
  background: ${theme.color.white};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${theme.borderRadius.small}px;
`;

const Header = styled.div`
  display: flex;
  flex-flow: column-reverse nowrap;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  margin-bottom: ${toRem(16)};

  @media (min-width: 500px) {
    flex-flow: row nowrap;
    align-items: center;
  }
`;

const SegmentInfo = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin: ${toRem(16)} 0 0 0;

  @media (min-width: 500px) {
    margin: 0 ${toRem(16)} 0 0;
  }
`;

const SegmentName = styled.div`
  margin-bottom: ${toRem(4)};
  font-size: ${toRem(12)};
  font-weight: ${theme.font.weight.bold};
`;

const EmployeeSegmentInfo = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;

  span {
    padding: 0 ${toRem(8)};
    font-size: ${toRem(12)};
    font-weight: ${theme.font.weight.regular};
    color: ${theme.color.neutral[500]};

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      padding-right: 0;
    }

    &:not(:last-child) {
      border-right: 1px solid ${theme.color.neutral[200]};
    }
  }
`;

const Semibold = styled.span`
  font-weight: ${theme.font.weight.semiBold};
`;

const ToggleableBreakdown: React.FC<IToggleableBreakdownProps> = ({
  categoryGroups,
  calculatorRules,
  calculation,
  creditCardFee,
  className = '',
  contributingOnly = false,
  dataCy = 'employee-tips-toggleable-breakdown',
  segmentName,
  disabled = false,
}) => {
  const [switchChecked, setSwitchChecked] = useState(!contributingOnly); // true === receiving, false === contributing

  const totalDuration = new Duration(calculation.totals.totalMinWorked);

  const onSwitchChange = () => setSwitchChecked(!switchChecked);

  // Build Contributing -> Collected
  const collectedLineItems: ITipsBreakdownLineItem[] = [
    { label: 'CC Tips', amount: calculation.totals.tip.creditCard || 0 },
    { label: 'CC Autograts', amount: calculation.totals.autoGratuity.creditCard || 0 },
    ...(calculation.totals.tip.creditCardFee || calculation.totals.autoGratuity.creditCardFee
      ? [
          {
            label: 'CC Fees',
            amount: -(calculation.totals.tip.creditCardFee || 0) + -(calculation.totals.autoGratuity.creditCardFee || 0),
            ...(creditCardFee && { description: `(${creditCardFee}%)` }),
          },
        ]
      : []),
    { label: 'Cash Tips', amount: calculation.totals.tip.cash || 0 },
    { label: 'Cash Autograts', amount: calculation.totals.autoGratuity.cash || 0 },
    { label: 'Net', amount: calculation.totals.net },
  ];

  // Build Contributing -> Contributing To
  const contributingToLineItems = (contributions: ITipCalculatorResultContribution[]): ITipsBreakdownLineItem[] =>
    (contributions || [])
      .map((contribution) => {
        // Build the label for contribution line items
        const jobTitles = contribution.beneficiaries.map((beneficiary) => toTitleCase(beneficiary.name));
        const rule = calculatorRules.find((rule) => rule.contributor && rule.contributor.name.trim().toLowerCase() === calculation.job.title.trim().toLowerCase());
        const beneficiary = rule.sharePolicy.shares.find((s) => s.beneficiaries.every((b) => jobTitles.map((j) => j.trim().toLowerCase()).includes(b.name.trim().toLowerCase())));
        const label = jobTitles.join(' & ');

        // Build the description
        const eligibilityMethod = contribution.eligibilityMethod === ShareEligibilityMethod.BY_SEGMENT ? 'segment' : 'check';
        const sources = getTipSources(beneficiary.sources);

        const description = (
          <>
            earning <Semibold>{toPercent(contribution.share)}</Semibold> of <Semibold>Eligible {contribution.type === ShareRuleType.TIP ? sources : `${toTitleCase(contribution.category)} Sales`}</Semibold>
            {' per'} {eligibilityMethod} ({centsToDollarsDisplay(contribution.eligibleAmount)})
          </>
        );

        return { label, amount: contribution.total, description };
      })
      .sort((a, b) => b.amount - a.amount);

  // Build Contributing -> Sales
  const salesLineItems = (calculation: ITipCalculatorResultEmployee, categoryGroups: ICategoryGroup[], rules: ICalculatorRule[]): ITipsBreakdownLineItem[] => {
    const includeSalesLineItems = rules.some((rule) => rule.sharePolicy?.shares.some((share) => [ShareRuleType.CATEGORY, ShareRuleType.CATEGORY_GROUP].includes(share.type)));

    if (!includeSalesLineItems) return undefined;

    return buildSalesItems(calculation, categoryGroups);
  };

  const remainderLineItems = (): ITipsBreakdownLineItem[] => {
    let result: ITipsBreakdownLineItem[];

    // Build Contributing -> Remainder
    const contributingTotals = (calculation.contributions || []).reduce(
      (acc, contribution) => {
        acc.net += contribution.total;
        contribution.sources.forEach((source) => {
          if (source.source === RuleFundSource.TIPS) acc.tips += Math.round(source.total);
          if (source.source === RuleFundSource.AUTO_GRATUITIES) acc.autoGratuities += Math.round(source.total);
        });
        return acc;
      },
      { net: 0, autoGratuities: 0, tips: 0 }
    );

    const jobWithRemainderSharePolicy = calculatorRules.find((rule) => rule.sharePolicy && rule.contributor && rule.contributor.name.toLowerCase() === calculation.job.title.toLowerCase());
    if (jobWithRemainderSharePolicy?.sharePolicy?.remainder.type === ShareRemainderType.SELF) {
      result = [
        {
          amount: calculation.retained.tips || 0,
          label: `Retained Tips by ${calculation.name}`,
        },
        {
          amount: calculation.retained.autoGratuities || 0,
          label: `Retained Autograts by ${calculation.name}`,
        },
      ];
    }

    if (jobWithRemainderSharePolicy?.sharePolicy?.remainder.type === ShareRemainderType.POOL) {
      const pooledJobs = jobWithRemainderSharePolicy.sharePolicy.remainder.beneficiaries.flatMap((beneficiary) => beneficiary.participants.map((p) => toTitleCase(p.name)));

      result = [
        {
          amount: calculation.totals.net ? calculation.totals.net - contributingTotals.net : 0,
          label: 'Pooled',
          description: pooledJobs.join(' & '),
        },
      ];
    }
    return result || [];
  };

  const setSplit = (splitMethod: CalculatorSplitMethod): string => {
    if (splitMethod === CalculatorSplitMethod.EVENLY) return ' split evenly';
    if (splitMethod === CalculatorSplitMethod.TIME_WORKED) return ' split by time worked';
    return '';
  };

  // Build Receiving -> Receiving From
  const receivingLineItems = (): ITipsBreakdownLineItem[] => {
    const result: ITipsBreakdownLineItem[] = [];
    // Build retained totals (tips)
    if (calculation.retained.tips) {
      result.push({
        label: 'Retained Tips',
        amount: calculation.retained.tips,
      });
    }

    // Build retained totals (Auto gratuties)
    if (calculation.retained.autoGratuities) {
      result.push({
        label: 'Retained Autograts',
        amount: calculation.retained.autoGratuities,
      });
    }

    calculation.received
      ?.filter((item) => [ShareRuleType.CATEGORY, ShareRuleType.CATEGORY_GROUP, ShareRuleType.TIP].includes(item.type))
      .forEach((received) => {
        // When received distribution is SHARE
        if (received.distribution === TipCalculatorReceivedDistributionType.SHARE) {
          const rule = calculatorRules.find((rule) => rule.contributor && rule.contributor.name.trim().toLowerCase() === received.from.name.trim().toLowerCase());
          const beneficiary = rule.sharePolicy.shares.find((s) => s.beneficiaries.find((b) => b.name.trim().toLowerCase() === calculation.job.title.trim().toLowerCase()));
          const sources = getTipSources(beneficiary.sources);
          const desc = [ShareRuleType.CATEGORY, ShareRuleType.CATEGORY_GROUP].includes(received.type) ? toTitleCase(received.category) : sources;

          result.push({
            label: `${toTitleCase(received.from.name)} Share`,
            amount: received.total,
            description: (
              <>
                earning <Semibold>{toPercent(received.share)}</Semibold> of <Semibold>{desc}</Semibold>
                {setSplit(received.split)}
              </>
            ),
          });
        }

        // When received type is TIP and distribution is REMAINDER
        // Currently, the distribution type of `REMAINDER` is only possible with a type of `TIP`
        if (received.type === ShareRuleType.TIP && received.distribution === TipCalculatorReceivedDistributionType.REMAINDER) {
          const condition = received.conditions ? received.conditions[0] : undefined;
          const rule = calculatorRules.find((rule) => rule.contributor && rule.contributor.name.trim().toLowerCase() === received.from.name.trim().toLowerCase());
          const beneficiary = rule.sharePolicy.remainder.beneficiaries.find((b) => b.participants.find((p) => p.name.trim().toLowerCase() === calculation.job.title.trim().toLowerCase()));
          const sources = getTipSources(beneficiary.sources);

          result.push({
            label: `${toTitleCase(received.from.name)} Share`,
            amount: received.total,
            description: (
              <>
                earning{' '}
                {condition && (
                  <>
                    <Semibold>{condition.hours} hours</Semibold> of{' '}
                  </>
                )}
                <Semibold>{sources}</Semibold>
                {setSplit(received.split)}
              </>
            ),
          });
        }
      });

    // Default Pool
    // Total the amounts received from the default pool as one received line.
    const defaultPoolReceived = calculation.received?.filter((item) => [ShareRuleType.DEFAULT_POOL].includes(item.type));
    if (defaultPoolReceived?.length) {
      const defaultPooltotal = defaultPoolReceived.reduce((acc, item) => acc + item.total, 0);

      result.push({
        label: DEFAULT_POOL_NAME,
        amount: defaultPooltotal,
      });
    }

    return result;
  };

  return (
    <ToggleableBreakdownLayout className={className} data-cy={dataCy}>
      <Header data-cy={`${dataCy}-header`}>
        <SegmentInfo>
          <SegmentName data-cy={`${dataCy}-segment-name`}>{segmentName}</SegmentName>
          <EmployeeSegmentInfo>
            <span data-cy={`${dataCy}-job-title`}>{toTitleCase(calculation.job.title)}</span>
            {!contributingOnly && <span data-cy={`${dataCy}-duration`}>{totalDuration.toString()}</span>}
          </EmployeeSegmentInfo>
        </SegmentInfo>

        {contributingOnly ? (
          <ContributingTag dataCy={`${dataCy}-contributing`} label="Contributing" />
        ) : (
          <StyledButtonSwitch dataCy={`${dataCy}-switch`} onChange={onSwitchChange} checked={switchChecked} leftText="Contributing" rightText="Receiving" data-cy="toggleable-breakdown-switch" disabled={disabled} />
        )}
      </Header>

      <BreakdownContainer>
        {switchChecked ? (
          <ReceivingBreakdown values={receivingLineItems()} />
        ) : (
          <ContributingBreakdown
            collectedLineItems={collectedLineItems}
            salesLineItems={salesLineItems(calculation, categoryGroups, calculatorRules)}
            contributingLineItems={calculation.contributions ? contributingToLineItems(calculation.contributions) : []}
            remainderLineItems={remainderLineItems()}
          />
        )}
      </BreakdownContainer>
    </ToggleableBreakdownLayout>
  );
};

export default ToggleableBreakdown;
